import React, { useMemo } from 'react';
import { differenceInDays, addDays, format, parseISO } from 'date-fns';

/**
 * Smart Milestone Timeline with:
 * 1. Collision detection - only stagger labels that would actually overlap
 * 2. Right-angle connector lines (vertical from dot to label)
 */
const TimelineBar = ({ project }) => {
  // Calculate date boundaries
  const today = useMemo(() => new Date(), []);
  // BUG-03 FIX: Use parseISO to avoid timezone off-by-one issues
  const projectEnd = useMemo(() => parseISO(project.end_date), [project.end_date]);
  const maxWindow = useMemo(() => addDays(today, 180), [today]);
  const windowEnd = useMemo(() => projectEnd < maxWindow ? projectEnd : maxWindow, [projectEnd, maxWindow]);
  const windowDays = useMemo(() => differenceInDays(windowEnd, today), [windowEnd, today]);
  const isLongProject = useMemo(() => differenceInDays(projectEnd, today) > 180, [projectEnd, today]);

  // Filter and prepare milestones
  const baseMilestones = useMemo(() => {
    return (project.milestones || [])
      .filter(m => {
        if (m.status === 'complete') return false;
        // BUG-03 FIX: Use parseISO
        const mDate = parseISO(m.target_date);
        return mDate >= today && mDate <= windowEnd;
      })
      .map((m) => {
        // BUG-03 FIX: Use parseISO
        const mDate = parseISO(m.target_date);
        const daysOut = differenceInDays(mDate, today);
        const position = windowDays > 0 ? (daysOut / windowDays) * 100 : 0;
        return { ...m, daysOut, position: Math.min(position, 100), date: mDate };
      })
      .sort((a, b) => a.daysOut - b.daysOut);
  }, [project.milestones, today, windowEnd, windowDays]);

  // Smart collision detection - calculate tiers based on actual overlap
  // Limit to 6 milestones to prevent overcrowding
  const milestonesWithTiers = useMemo(() => {
    if (baseMilestones.length === 0) return [];
    
    // Limit display to 6 milestones max for readability
    const displayMilestones = baseMilestones.slice(0, 6);

    const LABEL_WIDTH_PCT = 10; // Reduced label width for less collision (~70px)

    // Calculate label bounds as percentage
    const getLabelBounds = (pct) => ({
      left: pct - LABEL_WIDTH_PCT / 2,
      right: pct + LABEL_WIDTH_PCT / 2
    });

    // Check if two labels overlap (with small margin)
    const overlaps = (bounds1, bounds2) => {
      const margin = 1; // Reduced margin for tighter packing
      return !(bounds1.right + margin < bounds2.left || bounds1.left - margin > bounds2.right);
    };

    // Tier definitions:
    // 0: Default below bar (short connector)
    // 1: Lower below bar (tall connector)
    // 2: Above bar (short connector)
    // 3: Higher above bar (tall connector)
    
    // Track occupied positions at each tier
    const tierOccupancy = [[], [], [], []];
    const result = [];

    for (const milestone of displayMilestones) {
      const bounds = getLabelBounds(milestone.position);
      let assignedTier = 0;

      // Check each tier for available space, starting with below bar
      for (let tier = 0; tier <= 3; tier++) {
        const hasCollision = tierOccupancy[tier].some(occupied => overlaps(bounds, occupied));
        if (!hasCollision) {
          assignedTier = tier;
          break;
        }
      }

      tierOccupancy[assignedTier].push(bounds);

      result.push({
        ...milestone,
        tier: assignedTier,
        isAbove: assignedTier >= 2,
        connectorHeight: (assignedTier === 1 || assignedTier === 3) ? 45 : 16
      });
    }

    return result;
  }, [baseMilestones]);

  if (milestonesWithTiers.length === 0) return null;

  // Group milestones by position (above/below)
  const aboveMilestones = milestonesWithTiers.filter(m => m.isAbove);
  const belowMilestones = milestonesWithTiers.filter(m => !m.isAbove);

  // Calculate section heights
  const aboveHasTall = aboveMilestones.some(m => m.connectorHeight === 45);
  const belowHasTall = belowMilestones.some(m => m.connectorHeight === 45);

  // More aggressive truncation for crowded timelines
  const truncateName = (name, maxLen = 12) => {
    if (!name) return '';
    return name.length > maxLen ? name.substring(0, maxLen) + '…' : name;
  };
  
  // Show overflow message if milestones were truncated
  const overflowCount = baseMilestones.length - milestonesWithTiers.length;

  return (
    <div className="mb-8" data-testid="timeline-bar">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">
        {isLongProject ? 'Milestone Timeline (6-Month View)' : 'Milestone Timeline'}
        {overflowCount > 0 && (
          <span className="text-xs font-normal text-slate-500 ml-2">
            (+{overflowCount} more)
          </span>
        )}
      </h3>

      {/* ABOVE section: labels + connectors */}
      {aboveMilestones.length > 0 && (
        <div className="relative" style={{ height: aboveHasTall ? '85px' : '50px' }}>
          {aboveMilestones.map((m) => {
            const labelBottom = m.connectorHeight;
            return (
              <div
                key={`above-${m.id}`}
                className="absolute"
                style={{
                  left: `${m.position}%`,
                  bottom: 0,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                {/* Label */}
                <div className="text-center mb-1" style={{ minWidth: '70px', maxWidth: '80px' }}>
                  <div className="text-xs font-medium text-slate-700 truncate" title={m.name}>
                    {truncateName(m.name)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {format(m.date, 'MMM d')} · {m.daysOut}d
                  </div>
                </div>
                {/* Connector line */}
                <div 
                  style={{ 
                    width: '2px', 
                    height: `${labelBottom}px`,
                    backgroundColor: '#94a3b8'
                  }} 
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Date labels */}
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>Today · {format(today, 'MMM d')}</span>
        <span>{format(windowEnd, 'MMM d, yyyy')}</span>
      </div>

      {/* Timeline Bar with Markers */}
      <div className="relative">
        <div className="h-3 bg-blue-500 rounded-full" />
        {milestonesWithTiers.map((m) => (
          <div
            key={`marker-${m.id}`}
            className="absolute"
            style={{ left: `${m.position}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <div className={`w-4 h-4 border-2 border-white rounded-full shadow ${
              m.status === 'at_risk' ? 'bg-amber-500' : 'bg-blue-600'
            }`} />
          </div>
        ))}
      </div>

      {/* BELOW section: connectors + labels */}
      {belowMilestones.length > 0 && (
        <div className="relative" style={{ height: belowHasTall ? '85px' : '50px' }}>
          {belowMilestones.map((m) => {
            const connectorTop = m.connectorHeight;
            return (
              <div
                key={`below-${m.id}`}
                className="absolute"
                style={{
                  left: `${m.position}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                {/* Connector line */}
                <div 
                  style={{ 
                    width: '2px', 
                    height: `${connectorTop}px`,
                    backgroundColor: '#94a3b8'
                  }} 
                />
                {/* Label */}
                <div className="text-center mt-1" style={{ minWidth: '70px', maxWidth: '80px' }}>
                  <div className="text-xs font-medium text-slate-700 truncate" title={m.name}>
                    {truncateName(m.name)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {format(m.date, 'MMM d')} · {m.daysOut}d
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimelineBar;
